import { X, Phone, Globe, User } from 'lucide-react';
import type { DriverRecord } from '../../../types';

type ViewDriverModalProps = {
  driver: DriverRecord | null;
  isOpen: boolean;
  onClose: () => void;
};

const InfoRow = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
}) => (
  <div className="flex items-center justify-between py-1">
    <span className="font-host text-[14px] font-medium text-[#757575]">{label}</span>
    <span className="font-host text-[14px] font-medium text-[#1A1A1A] flex items-center gap-1.5 text-right">
      {Icon && <Icon size={14} className="text-[#1A1A1A]" />}
      {value}
    </span>
  </div>
);

export const ViewDriverModal = ({ driver, isOpen, onClose }: ViewDriverModalProps) => {
  if (!isOpen || !driver) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div
        className="flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[24px] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between rounded-t-[24px] border-b border-[#F0F0F0] bg-white px-6 py-5">
          <h2 className="font-host text-[20px] font-bold text-[#1A1A1A]">Driver Details</h2>
          <button
            onClick={onClose}
            className="w-[32px] h-[32px] rounded-full bg-[#1A1A1A] flex items-center justify-center text-white hover:bg-[#333333] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col gap-8 overflow-y-auto overscroll-contain p-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {/* Top Box: Driver & Contact Info */}
          <div className="w-full rounded-[16px] bg-[#F2F2F2] p-5 flex items-start gap-6">
            <div className="w-[100px] h-[100px] shrink-0 rounded-[16px] bg-white border border-[#E0E0E0] flex items-center justify-center p-2 shadow-sm overflow-hidden">
              {driver.avatar && !driver.avatar.includes('driver-avatar.png') ? (
                <img
                  src={driver.avatar}
                  alt={driver.name}
                  className="w-full h-full rounded-[12px] object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-[12px] bg-[#EAEAEA] border border-[#DDDDDD] flex items-center justify-center text-[#666666]">
                  <User size={44} className="text-[#666666]" />
                </div>
              )}
            </div>

            <div className="flex flex-col flex-1 gap-2">
              <InfoRow label="Driver ID" value={driver.code} />
              <InfoRow label="Driver name" value={driver.name} />
              <InfoRow label="Mobile Number" value={driver.phone} icon={Phone} />
              <InfoRow label="Availability" value={driver.availability.toUpperCase()} />
            </div>
          </div>

          {/* Middle Box: License & Document */}
          <div className="flex flex-col gap-3">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">License & Document</h3>
            <div className="w-full rounded-[16px] bg-[#EEEEEE] p-5 flex flex-col gap-3">
              <InfoRow label="License Number" value="AT-9876543" />
              <InfoRow label="Issue Date" value="16/10/2024" />
              <InfoRow label="Expiry Date" value="12/10/2027" />
            </div>
          </div>

          {/* Bottom Box: Vehicle Information */}
          <div className="flex flex-col gap-3">
            <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Vehicle Information</h3>
            <div className="w-full rounded-[16px] bg-[#EEEEEE] p-5 flex flex-col gap-3">
              <InfoRow label="Registration Number" value={driver.vehicle.plate} />
              <InfoRow label="Vehicle Type" value={driver.vehicle.category} />
              <InfoRow
                label="Experience"
                value={`${driver.experienceYears} Years`}
                icon={Globe}
              />
              <InfoRow label="Languages" value={driver.language} icon={Globe} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end rounded-b-[24px] border-t border-[#F0F0F0] bg-white px-6 py-5">
          <button
            onClick={onClose}
            className="w-[140px] h-[48px] rounded-[24px] border border-[#E0E0E0] bg-white font-host text-[15px] font-bold text-[#1A1A1A] hover:bg-[#F9F9F9] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};